/**
 * @component ry-carousel
 * @category  display
 * @summary   Content slider (ry-ui-only). Children are wrapped into a horizontal track and shown one at a time via `translateX`. Supports autoplay with configurable interval, touch/swipe via pointer events, dot indicators, prev/next arrows, keyboard arrow navigation, optional loop wrapping, and pause-on-hover for autoplay.
 *
 * @attribute autoplay        boolean   default=false   Auto-advance the slides on a timer.
 * @attribute interval        string?   default="5000"  Milliseconds between auto-advances. Honored only when `[autoplay]` is set.
 * @attribute dots            boolean   default=false   Render the dot indicators row below the viewport. Clicking a dot calls `goTo(i)`.
 * @attribute arrows          boolean   default=false   Render the prev/next chevron buttons over the viewport.
 * @attribute loop            boolean   default=false   Wrap around when going past first/last slide. Without this, `goTo(-1)` and `goTo(slideCount)` are silent no-ops.
 * @attribute pause-on-hover  boolean   default=false   Stop autoplay while the mouse is over the carousel; resume on leave. Has no effect without `[autoplay]`.
 *
 * @method    goTo(index)   Move to a specific slide. In loop mode, wraps; otherwise clamps the range to a silent no-op. Fires `ry:change`.
 * @method    next()        Advance one slide. Wraps when `[loop]` is set.
 * @method    prev()        Step back one slide.
 *
 * @event ry:beforechange
 *        detail: { index: number, slide: number, previous: number }
 *        fires:  at the top of `goTo()` — next, prev, dot click, arrow keys, swipe, autoplay tick — after range/loop resolution but before the track moves. `index`/`slide` are the slide ABOUT to show; `previous` is the current index.
 *        cancelable: preventDefault() keeps the current slide; `ry:change` does not fire. An autoplay tick that is vetoed just skips — the timer keeps running.
 *
 * @event ry:change
 *        detail: { index: number, slide: number }
 *        fires:  after every successful slide transition (next, prev, goTo, swipe, autoplay tick, dot click). `index` and `slide` both equal the new active index.
 *
 * @keyboard  Host has `tabindex="0"`. ArrowLeft — prev. ArrowRight — next. No Home/End for direct jumps (use `el.goTo(0)` programmatically).
 *
 * @content   Direct children are slides. Setup wraps each in a `.ry-carousel__slide` cell inside a `.ry-carousel__track`. Children can be any element — `<div>`, `<img>`, `<ry-card>`, etc.
 *
 * @dom
 *   ry-carousel[autoplay?][interval?][dots?][arrows?][loop?][pause-on-hover?]   tabindex="0"
 *     > div.ry-carousel__viewport   [data-ry-target="viewport"]
 *       > div.ry-carousel__track    [data-ry-target="track"]   style="transform: translateX(-N%)"
 *         > div.ry-carousel__slide  [data-ry-target="slide"]   (one per child)
 *           > {authored child}
 *     > button.ry-carousel__arrow.ry-carousel__arrow--prev   [data-ry-target="prev"]   (only when [arrows])
 *     > button.ry-carousel__arrow.ry-carousel__arrow--next   [data-ry-target="next"]   (only when [arrows])
 *     > div.ry-carousel__dots   [data-ry-target="dots"]   (only when [dots])
 *       > button.ry-carousel__dot   [data-ry-target="dot"]   data-active?   (one per slide)
 *
 * @gotcha  `goTo(index)` is the only path that fires `ry:change`. `next()`, `prev()`, swipe-end, autoplay tick, dot click ALL route through `goTo()`. Without `[loop]`, out-of-range indices are SILENT no-ops — no `ry:change` fires for them.
 *
 * @gotcha  Swipe threshold is 50px horizontal. Smaller swipes are ignored (slide stays). Vertical drag is not modelled — vertical swipes do nothing.
 *
 * @gotcha  Setup wraps children in `.ry-carousel__slide` cells, which means the original child elements are now nested one level deeper. CSS targeting direct children of `<ry-carousel>` won't reach them — query through `[data-ry-target="slide"] > *`.
 *
 * @gotcha  Adding slides post-mount is not supported — the wrapper builds its slide list once. `slideCount` is captured at setup. To change the slide set, reconstruct the element.
 *
 * @gotcha  `[pause-on-hover]` requires `[autoplay]` to have any effect. Without autoplay the listeners are wasted but harmless.
 *
 * @gotcha  Touch/pointer interactions need real layout; happy-dom can't simulate `pointerdown → pointermove → pointerup` with realistic clientX values. Vitest covers programmatic + keyboard + dot click; cover swipe with Playwright if needed.
 *
 * @example basic
 * ```html
 * <ry-carousel arrows dots>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 *   <div>Slide 3</div>
 * </ry-carousel>
 * ```
 *
 * @example autoplay
 * ```html
 * <ry-carousel autoplay interval="3000" dots loop pause-on-hover>
 *   <img src="/a.jpg" alt="A">
 *   <img src="/b.jpg" alt="B">
 * </ry-carousel>
 * ```
 *
 * @example no-loop
 * ```html
 * <ry-carousel arrows>
 *   <div>1</div><div>2</div><div>3</div>
 * </ry-carousel>
 * ```
 *
 * @tutorial mount-wraps-children-in-track-and-slides
 * ```js
 * const el = mount(`
 *   <ry-carousel>
 *     <div>A</div>
 *     <div>B</div>
 *   </ry-carousel>
 * `);
 * const viewport = el.querySelector('[data-ry-target="viewport"]');
 * const track = el.querySelector('[data-ry-target="track"]');
 * await expectEqual(!!viewport, true);
 * await expectEqual(!!track, true);
 * const slides = el.querySelectorAll('[data-ry-target="slide"]');
 * await expectEqual(slides.length, 2);
 * ```
 *
 * @tutorial host-gets-tabindex-0
 * ```js
 * const el = mount('<ry-carousel><div>x</div></ry-carousel>');
 * await expectAttr(el, 'tabindex', '0');
 * ```
 *
 * @tutorial arrows-render-when-attribute-set
 * ```js
 * const el = mount('<ry-carousel arrows><div>A</div><div>B</div></ry-carousel>');
 * await expectEqual(!!el.querySelector('[data-ry-target="prev"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="next"]'), true);
 * ```
 *
 * @tutorial dots-render-one-per-slide
 * ```js
 * const el = mount('<ry-carousel dots><div>1</div><div>2</div><div>3</div></ry-carousel>');
 * const dots = el.querySelectorAll('[data-ry-target="dot"]');
 * await expectEqual(dots.length, 3);
 * await expectAttr(dots[0], 'data-active', '');
 * await expectEqual(dots[1].hasAttribute('data-active'), false);
 * ```
 *
 * @tutorial next-and-prev-move-the-active-slide
 * ```js
 * const el = mount('<ry-carousel><div>A</div><div>B</div><div>C</div></ry-carousel>');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.index));
 * el.next();
 * await expectEqual(fired[0], 1);
 * el.next();
 * await expectEqual(fired[1], 2);
 * el.prev();
 * await expectEqual(fired[2], 1);
 * ```
 *
 * @tutorial no-loop-out-of-range-is-silent
 * ```js
 * // Wrong assumption: prev() from index 0 silently goes to last. Without
 * // [loop], it's a no-op (no ry:change fired).
 * const el = mount('<ry-carousel><div>A</div><div>B</div></ry-carousel>');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * el.prev();
 * await expectEqual(fired.length, 0, 'no-op without [loop]');
 * el.next();
 * el.next();
 * el.next();
 * // Wrap around: B → no advance past end without loop
 * await expectEqual(fired.length, 1, 'only one advance fired');
 * ```
 *
 * @tutorial loop-wraps-past-edges
 * ```js
 * const el = mount('<ry-carousel loop><div>A</div><div>B</div></ry-carousel>');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.index));
 * el.prev();
 * await expectEqual(fired[0], 1, 'prev from 0 wraps to last');
 * el.next();
 * await expectEqual(fired[1], 0, 'next from last wraps to first');
 * ```
 *
 * @tutorial dot-click-jumps-to-slide
 * ```js
 * const el = mount('<ry-carousel dots><div>A</div><div>B</div><div>C</div></ry-carousel>');
 * const dots = el.querySelectorAll('[data-ry-target="dot"]');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.index));
 * dots[2].click();
 * await expectEqual(fired[0], 2);
 * await expectAttr(dots[2], 'data-active', '');
 * ```
 *
 * @tutorial track-transform-reflects-active-slide
 * ```js
 * const el = mount('<ry-carousel><div>A</div><div>B</div><div>C</div></ry-carousel>');
 * const track = el.querySelector('[data-ry-target="track"]');
 * await expectEqual(track.style.transform, 'translateX(-0%)', 'starts at 0');
 * el.goTo(1);
 * await expectEqual(track.style.transform, 'translateX(-100%)', 'second slide');
 * el.goTo(2);
 * await expectEqual(track.style.transform, 'translateX(-200%)', 'third slide');
 * ```
 *
 * @tutorial keyboard-arrows-navigate
 * ```js
 * const el = mount('<ry-carousel><div>A</div><div>B</div><div>C</div></ry-carousel>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * const t = el.querySelector('[data-ry-target="track"]');
 * await expectEqual(t.style.transform, 'translateX(-100%)');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowLeft', bubbles: true }));
 * await expectEqual(t.style.transform, 'translateX(-0%)');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange pins the current slide — whatever
 * // asked for the move (next(), arrows, dots, autoplay). ry:change stays silent.
 * const el = mount('<ry-carousel><div>A</div><div>B</div><div>C</div></ry-carousel>');
 * const track = el.querySelector('[data-ry-target="track"]');
 * const changed = [];
 * el.addEventListener('ry:change', (e) => changed.push(e.detail.index));
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.detail.index === 1 && e.detail.previous === 0) e.preventDefault();
 * }, { once: true });
 * el.next();
 * await expectEqual(track.style.transform, 'translateX(-0%)', 'vetoed move leaves slide 0');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * el.next();
 * await expectEqual(track.style.transform, 'translateX(-100%)', 'without the veto, next() advances');
 * await expectEqual(changed[0], 1, 'ry:change fired with the new index');
 * ```
 *
 * @see ry-tabs
 * @see ry-reveal
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export class RyCarousel extends RyElement {
  #currentIndex = 0;
  #slideCount = 0;
  #autoplayTimer: ReturnType<typeof setInterval> | null = null;
  #startX = 0;
  #isDragging = false;

  setup(): void {
    this.#transformMarkup();
    this.#setupAutoplay();

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    this.on(this, 'keydown', this.#handleKeydown as EventListener);
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="viewport"]')) return;

    const slides = [...this.children];
    this.#slideCount = slides.length;
    if (this.#slideCount === 0) return;

    const showArrows = this.hasAttribute('arrows');
    const showDots = this.hasAttribute('dots');

    // Wrap slides in track
    const track = document.createElement('div');
    track.setAttribute('data-ry-target', 'track');
    track.className = 'ry-carousel__track';
    for (const slide of slides) {
      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-ry-target', 'slide');
      wrapper.className = 'ry-carousel__slide';
      wrapper.appendChild(slide);
      track.appendChild(wrapper);
    }

    // Build viewport
    const viewport = document.createElement('div');
    viewport.setAttribute('data-ry-target', 'viewport');
    viewport.className = 'ry-carousel__viewport';
    viewport.appendChild(track);

    this.innerHTML = '';
    this.appendChild(viewport);

    // Arrows
    if (showArrows) {
      const prev = document.createElement('button');
      prev.setAttribute('data-ry-target', 'prev');
      prev.className = 'ry-carousel__arrow ry-carousel__arrow--prev';
      prev.setAttribute('aria-label', 'Previous slide');
      prev.setAttribute('type', 'button');
      prev.innerHTML = getIcon('chevron-left');
      this.appendChild(prev);
      this.on(prev, 'click', () => this.prev());

      const next = document.createElement('button');
      next.setAttribute('data-ry-target', 'next');
      next.className = 'ry-carousel__arrow ry-carousel__arrow--next';
      next.setAttribute('aria-label', 'Next slide');
      next.setAttribute('type', 'button');
      next.innerHTML = getIcon('chevron-right');
      this.appendChild(next);
      this.on(next, 'click', () => this.next());
    }

    // Dots
    if (showDots) {
      const dotsContainer = document.createElement('div');
      dotsContainer.setAttribute('data-ry-target', 'dots');
      dotsContainer.className = 'ry-carousel__dots';
      for (let i = 0; i < this.#slideCount; i++) {
        const dot = document.createElement('button');
        dot.setAttribute('data-ry-target', 'dot');
        dot.className = 'ry-carousel__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('type', 'button');
        if (i === 0) dot.setAttribute('data-active', '');
        this.on(dot, 'click', () => this.goTo(i));
        dotsContainer.appendChild(dot);
      }
      this.appendChild(dotsContainer);
    }

    // Touch/swipe
    this.on(viewport, 'pointerdown', this.#handlePointerDown as EventListener);
    this.on(viewport, 'pointermove', this.#handlePointerMove as EventListener);
    this.on(viewport, 'pointerup', this.#handlePointerUp as EventListener);
    this.on(viewport, 'pointercancel', this.#handlePointerUp as EventListener);

    // Pause on hover
    if (this.hasAttribute('pause-on-hover')) {
      this.on(this, 'mouseenter', () => this.#stopAutoplay());
      this.on(this, 'mouseleave', () => this.#setupAutoplay());
    }

    this.goTo(0);
  }

  #handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    }
  };

  #handlePointerDown = (e: PointerEvent): void => {
    this.#startX = e.clientX;
    this.#isDragging = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  #handlePointerMove = (e: PointerEvent): void => {
    if (!this.#isDragging) return;
    // Prevent scroll while swiping
    e.preventDefault();
  };

  #handlePointerUp = (e: PointerEvent): void => {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    const diff = e.clientX - this.#startX;
    const threshold = 50;
    if (diff > threshold) {
      this.prev();
    } else if (diff < -threshold) {
      this.next();
    }
  };

  #setupAutoplay(): void {
    this.#stopAutoplay();
    if (!this.hasAttribute('autoplay')) return;
    const interval = parseInt(this.getAttribute('interval') ?? '5000', 10);
    this.#autoplayTimer = setInterval(() => this.next(), interval);
  }

  #stopAutoplay(): void {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  goTo(index: number): void {
    const loop = this.hasAttribute('loop');
    if (loop) {
      if (index < 0) index = this.#slideCount - 1;
      if (index >= this.#slideCount) index = 0;
    } else {
      if (index < 0 || index >= this.#slideCount) return;
    }

    if (!this.emit('beforechange', { index, slide: index, previous: this.#currentIndex })) return;

    this.#currentIndex = index;

    const track = this.$<HTMLElement>('[data-ry-target="track"]');
    if (track) {
      track.style.transform = `translateX(-${index * 100}%)`;
    }

    // Update dots
    const dots = this.$$('[data-ry-target="dot"]');
    dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('data-active', '');
      } else {
        dot.removeAttribute('data-active');
      }
    });

    this.emit('change', { index, slide: this.#currentIndex });
  }

  next(): void {
    this.goTo(this.#currentIndex + 1);
  }

  prev(): void {
    this.goTo(this.#currentIndex - 1);
  }

  teardown(): void {
    this.#stopAutoplay();
  }
}

customElements.define('ry-carousel', RyCarousel);
